'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The camera lock, and how an operator gets it back.
 *
 * Manual flight draws its grab halo at the CENTRE of the map, which is only
 * honest while the aircraft is held there. But a map an operator cannot pan is
 * a map they cannot use to look at the thing they are flying towards — so in
 * the MAP and SPLIT layouts the lock is releasable: touch the map and the
 * camera is yours, press RECENTER and it goes back, and it goes back on its own
 * after `MANUAL_RELOCK_MS` of no interaction.
 *
 * The auto re-lock is the whole point of the hook. Without it the operator
 * pans, looks, then reaches for the aircraft symbol — and drags from a halo
 * that is now over empty map, because the symbol left the screen while they
 * were looking. The timer means the surface repairs itself while they are
 * still deciding.
 *
 * In the VIDEO layout the minimap is a CONTROL, not a map to explore: it is
 * passed `releasable: false` and the lock can never be given up.
 */

/**
 * Idle time before the camera goes back to the aircraft, ms.
 *
 * Long enough to read a rooftop or a road junction, short enough that an
 * operator who looked away and then reached for the sticks finds the symbol
 * where they left it. Fixed at 8 s by the feature spec.
 */
export const MANUAL_RELOCK_MS = 8000;

export interface ManualMapLock {
  /** True while the camera is held on the aircraft. */
  locked: boolean;
  /** The operator touched the map. No-op when the lock cannot be released. */
  interact: () => void;
  /** RECENTER: lock now, and cancel the pending automatic re-lock. */
  recenter: () => void;
}

export function useManualMapLock({
  enabled,
  releasable,
  relockMs = MANUAL_RELOCK_MS,
}: {
  /** Manual authority is held. With no session there is no camera to lock. */
  enabled: boolean;
  /** False in the control minimap — there, the lock is not the operator's to give up. */
  releasable: boolean;
  /** Injected for tests. */
  relockMs?: number;
}): ManualMapLock {
  const [locked, setLocked] = useState(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const recenter = useCallback(() => {
    clearTimer();
    setLocked(true);
  }, [clearTimer]);

  const interact = useCallback(() => {
    if (!enabled || !releasable) return;
    setLocked(false);
    // Restarted on every touch, so a series of pans is one 8-second window from
    // the LAST of them rather than from the first.
    clearTimer();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setLocked(true);
    }, relockMs);
  }, [clearTimer, enabled, releasable, relockMs]);

  // Leaving manual control, or entering a layout where the lock is not
  // releasable, re-locks immediately: a pending timer from the previous layout
  // must never hand the camera back three seconds into the next one.
  useEffect(() => {
    if (!enabled || !releasable) recenter();
  }, [enabled, releasable, recenter]);

  useEffect(() => clearTimer, [clearTimer]);

  return { locked: enabled ? locked : true, interact, recenter };
}
